import clsx from "clsx";
import { makeStyles } from "tss-react/mui";

import { Box, styled, TableHead, Typography } from "@mui/material";
import { flexRender, Header as TanstackHeader } from "@tanstack/react-table";

import { MinimalTableHeaderCell } from "./MinimalTableComponents";

type Alignment = "left" | "right" | "center";

const getJustifyContent = (align: Alignment) => {
    switch (align) {
        case "right":
            return "flex-end";
        case "center":
            return "center";
        default:
            return "flex-start";
    }
};

const useStyles = makeStyles()((theme) => ({
    pointingCursor: { cursor: "pointer" },
    defaultCursor: { cursor: "default" },
    icon: {
        fontSize: 16,
        color: theme.palette.emteria.action,
    },
    title: {
        fontWeight: 600,
        fontSize: 16,
        lineHeight: 1.2,
        margin: 0,
    },
    sortIconWrap: {
        display: "inline-flex",
        alignItems: "center",
        alignSelf: "center",
        lineHeight: 0,
    },
    content: {
        display: "flex",
        alignItems: "center",
        height: "100%",
        maxHeight: "50px",
        gap: theme.spacing(1),
        padding: theme.spacing(3),
        paddingLeft: 0,
    },
}));

const Header = <T,>(props: React.PropsWithChildren<TanstackHeader<T, unknown>>) => {
    const { classes } = useStyles();
    const canSort = props.column.getCanSort();

    type ColumnMeta = { align?: Alignment };
    const meta = props.column.columnDef.meta as ColumnMeta | undefined;
    const align = meta?.align || "left";

    return (
        <MinimalTableHeaderCell title="" key={props.id} colSpan={props.colSpan} align={align}>
            <Box className={classes.content} sx={{ justifyContent: getJustifyContent(align) }}>
                <Typography
                    component="div"
                    className={clsx(classes.title, canSort ? classes.pointingCursor : classes.defaultCursor)}
                    onClick={props.column.getToggleSortingHandler()}
                >
                    {flexRender(props.column.columnDef.header, props.getContext())}
                </Typography>
                <span className={classes.sortIconWrap}>
                    {{
                        asc: <i className={clsx(classes.icon, "fa-solid fa-arrow-up")} />,
                        desc: <i className={clsx(classes.icon, "fa-solid fa-arrow-down")} />,
                    }[props.column.getIsSorted() as string] ?? null}
                </span>
                {props.children}
            </Box>
        </MinimalTableHeaderCell>
    );
};

type TableHeaderProps<T> = TanstackHeader<T, unknown> & {
    sortDirection: string;
    sortCriteria: string;
    onSort: (id: string) => void;
};
const CustomTableHeader = <T,>(props: React.PropsWithChildren<TableHeaderProps<T>>) => {
    const { classes } = useStyles();
    const canSort = props.column.getCanSort();

    type ColumnMeta = { align?: Alignment };
    const meta = props.column.columnDef.meta as ColumnMeta | undefined;
    const align = meta?.align || "left";

    const onSort = () => {
        if (canSort) {
            props.onSort(props.column.id);
        }
    };

    return (
        <MinimalTableHeaderCell title="" key={props.id} colSpan={props.colSpan} align={align}>
            <Box className={classes.content} sx={{ justifyContent: getJustifyContent(align) }}>
                <Typography
                    component="div"
                    className={clsx(classes.title, canSort ? classes.pointingCursor : classes.defaultCursor)}
                    onClick={onSort}
                >
                    {flexRender(props.column.columnDef.header, props.getContext())}
                </Typography>
                <span className={classes.sortIconWrap}>
                    {props.sortDirection === "asc" && props.sortCriteria === props.id ? (
                        <i className={clsx(classes.icon, "fa-solid fa-arrow-up")} />
                    ) : props.sortDirection === "desc" && props.sortCriteria === props.id ? (
                        <i className={clsx(classes.icon, "fa-solid fa-arrow-down")} />
                    ) : null}
                </span>
                {props.children}
            </Box>
        </MinimalTableHeaderCell>
    );
};

const LegacyHeader = styled(TableHead)(({ theme }) => ({
    "& > .MuiTableRow-root": {
        "& > .MuiTableCell-head": {
            backgroundColor: theme.palette.emteria.background.tableHeaders,
            color: theme.palette.emteria.text.primary + " !important",
            "& .MuiTableSortLabel-root": {
                color: theme.palette.emteria.text.primary + " !important",
                "&:hover": {
                    color: theme.palette.emteria.text.primary + " !important",
                },
                "&.MuiTableSortLabel-active": {
                    color: theme.palette.emteria.text.primary + " !important",
                },
                "& .MuiTableSortLabel-icon": {
                    color: "inherit !important",
                },
            },
        },
    },
}));

export { CustomTableHeader, Header, LegacyHeader as TableHeader };
